import { useMutation } from '@tanstack/react-query';

import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';

/**
 * Variables for `useScheduleDemoMutation`.
 *
 * `companyId` is the lead/company id (required by the endpoint path).
 * `contactId` identifies the associated contact for the demo; the legacy
 * `loadMeetingLink` makes this optional but the UI always requires it
 * before calling. `timeZone` defaults on the caller side to the browser's
 * timezone if not provided; the server will fall back to a sane default.
 */
export type UseScheduleDemoVariables = {
	companyId: string;
	contactId?: string;
	timeZone?: string;
};

export type ScheduleDemoResult = {
	scheduledMeetingId?: string;
	/** Full URL to launch in a new tab. */
	url: string;
};

/**
 * POSTs `lead/{companyId}/meetingScheduler` to obtain a one-shot demo
 * scheduling URL for the rep to open in a new tab. Mirrors the legacy
 * `FormFieldViewModel.loadMeetingLink` (see
 * `aida/viewModels/form.ts:338-365`) but as a clean react-query mutation
 * with no MobX coupling.
 *
 * The endpoint accepts `contactId` and `timeZone` as query parameters
 * (matching the legacy URL composition) — no request body.
 */
export function useScheduleDemoMutation() {
	const userSession = useUserSession();

	return useMutation<ScheduleDemoResult, Error, UseScheduleDemoVariables>({
		mutationFn: async ({ companyId, contactId, timeZone }) => {
			const queryParams: Record<string, string> = {};
			if (contactId) queryParams.contactId = contactId;
			if (timeZone) queryParams.timeZone = timeZone;

			const result = await userSession.webServiceHelper.callWebServiceAsync<ScheduleDemoResult>(
				ImpersonationBroker.composeApiUrl({
					urlPath: `lead/${companyId}/meetingScheduler`,
					queryParams,
				}),
				'POST'
			);
			if (!result.success) {
				throw asApiError(result);
			}
			if (!result.value?.url) {
				throw new Error('Demo scheduler did not return a URL.');
			}
			return result.value;
		},
	});
}
